import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Ban, Calendar, Image as ImageIcon, Trophy, Edit, Trash2, MoreVertical } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useIsMobile } from '../hooks/useIsMobile';
import { notifyDataChanged } from '../utils/dataBus';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const Historique = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile(640);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedRubrique, setSelectedRubrique] = useState('all');
  const [rubriques, setRubriques] = useState([]);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [receiptImage, setReceiptImage] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteTournamentDialog, setShowDeleteTournamentDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [showEditDateDialog, setShowEditDateDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Filtrage mensuel
  const [selectedMonth, setSelectedMonth] = useState('current'); // 'current', 'previous' ou 'custom'
  const [customMonth, setCustomMonth] = useState(null); // { year: 2024, month: 10 }
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);

  useEffect(() => {
    fetchRubriques();
    fetchTransactions();
    fetchTournaments();
  }, [selectedMonth]);

  const fetchRubriques = async () => {
    try {
      const response = await axios.get(`${API}/rubriques`, { withCredentials: true });
      setRubriques(response.data);
    } catch (error) {
      console.error('Erreur rubriques:', error);
    }
  };

  const getMonthDates = (monthType) => {
    const now = new Date();
    let start, end;
    
    if (monthType === 'current') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    } else if (monthType === 'previous') {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    } else if (monthType === 'custom' && customMonth) {
      start = new Date(customMonth.year, customMonth.month - 1, 1);
      end = new Date(customMonth.year, customMonth.month, 0, 23, 59, 59);
    }
    
    return { start, end };
  };

  // Générer la liste des mois disponibles (12 derniers mois, ordre chronologique)
  const getAvailableMonths = () => {
    const months = [];
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const previousYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    
    // Générer les 12 derniers mois
    for (let i = 0; i < 12; i++) {
      const targetDate = new Date(currentYear, currentMonth - 1 - i, 1);
      const year = targetDate.getFullYear();
      const month = targetDate.getMonth() + 1;
      
      // Exclure mois en cours et mois précédent
      if ((year === currentYear && month === currentMonth) || 
          (year === previousYear && month === previousMonth)) {
        continue;
      }
      
      months.push({
        year,
        month,
        label: targetDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      });
    }
    
    // Inverser pour avoir l'ordre chronologique (plus ancien au plus récent)
    return months.reverse();
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {};
      
      // Filtrage mensuel
      if (selectedMonth) {
        const { start, end } = getMonthDates(selectedMonth);
        params.start_date = start.toISOString();
        params.end_date = end.toISOString();
      } else if (startDate && endDate) {
        params.start_date = new Date(startDate).toISOString();
        params.end_date = new Date(endDate).toISOString();
      } else if (startDate) {
        params.start_date = new Date(startDate).toISOString();
      } else if (endDate) {
        params.end_date = new Date(endDate).toISOString();
      }
      
      const response = await axios.get(`${API}/transactions`, {
        params,
        withCredentials: true
      });
      
      let filteredTransactions = response.data;
      
      // Filtrer par rubrique si sélectionnée
      if (selectedRubrique !== 'all') {
        filteredTransactions = filteredTransactions.filter(trans => 
          trans.detailsCA && trans.detailsCA[selectedRubrique]
        );
      }
      
      // Tri explicite par date décroissante (plus récentes en premier)
      filteredTransactions.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA; // Ordre décroissant
      });
      
      setTransactions(filteredTransactions);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      const params = {};
      
      // Filtrage mensuel
      if (selectedMonth) {
        const { start, end } = getMonthDates(selectedMonth);
        params.start_date = start.toISOString();
        params.end_date = end.toISOString();
      } else if (startDate && endDate) {
        params.start_date = new Date(startDate).toISOString();
        params.end_date = new Date(endDate).toISOString();
      } else if (startDate) {
        params.start_date = new Date(startDate).toISOString();
      } else if (endDate) {
        params.end_date = new Date(endDate).toISOString();
      }
      
      const response = await axios.get(`${API}/tournaments`, {
        params,
        withCredentials: true
      });
      
      // Tri explicite par date décroissante (plus récentes en premier)
      const sortedTournaments = response.data.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA; // Ordre décroissant
      });
      
      setTournaments(sortedTournaments);
    } catch (error) {
      console.error('Erreur tournaments:', error);
    }
  };

  const handleViewReceipt = async (transactionId) => {
    try {
      const response = await axios.get(`${API}/transactions/${transactionId}/image`, { withCredentials: true });
      setReceiptImage(response.data.image);
      setShowReceiptDialog(true);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement du reçu');
    }
  };

  const handleDelete = async () => {
    if (!selectedTransaction) return;

    try {
      await axios.delete(`${API}/transactions/${selectedTransaction.id}`, { withCredentials: true });
      toast.success('Vente supprimée définitivement');
      notifyDataChanged('transaction');
      setShowDeleteDialog(false);
      setSelectedTransaction(null);
      fetchTransactions();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteTournament = async () => {
    if (!selectedTournament) return;

    try {
      await axios.delete(`${API}/tournaments/${selectedTournament.id}`, { withCredentials: true });
      toast.success('Tournoi supprimé définitivement');
      notifyDataChanged('tournoi');
      setShowDeleteTournamentDialog(false);
      setSelectedTournament(null);
      fetchTournaments();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleEditDate = async (dateToUse = null) => {
    const dateForUpdate = dateToUse || selectedDate;
    if (!selectedTransaction || !dateForUpdate) return;

    try {
      await axios.patch(`${API}/transactions/${selectedTransaction.id}/date`, 
        { date: dateForUpdate.toISOString() }, 
        { withCredentials: true }
      );
      toast.success('Date modifiée avec succès');
      notifyDataChanged('date-modifiee');
      setShowEditDateDialog(false);
      setSelectedTransaction(null);
      setSelectedDate(new Date());
      
      // Petit délai pour s'assurer que la DB est mise à jour
      setTimeout(() => {
        fetchTransactions();
        fetchTournaments();
      }, 500);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  return (
    <div className="space-y-6 pb-20 xl:pb-8" data-testid="historique-page">
      {/* Header ULTRA PREMIUM - RESPONSIVE */}
      <div className="relative">
        <div className="absolute -inset-1 bg-gradient-to-r from-[#C6A15B] via-[#FFD700] to-[#C6A15B] rounded-lg blur opacity-20 animate-pulse"></div>
        <div className="relative bg-gradient-to-r from-[#1C2541] to-[#2A3B5C] p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl border border-[#C6A15B]/30">
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700]">
            💰 Encaissements
          </h1>
          <p className="text-[#C6A15B]/80 mt-1 sm:mt-2 text-xs sm:text-sm">Toutes vos transactions en un coup d'œil</p>
        </div>
      </div>

      {/* Filtrage Mensuel ULTRA PREMIUM - RESPONSIVE */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/20 via-[#C6A15B]/20 to-[#FFD700]/20 rounded-2xl sm:rounded-3xl blur-2xl animate-pulse"></div>
        <Card className="relative glass-effect border-2 sm:border-3 border-[#C6A15B]/50 shadow-2xl">
          <CardContent className="p-3 sm:p-4 md:p-6">
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-2 sm:gap-3">
              {/* Dropdown Premium "Choisir Un Mois" - RESPONSIVE */}
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                  className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-sm sm:text-base transition-all duration-300 transform hover:scale-105 ${
                    selectedMonth === 'custom'
                      ? 'bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700] text-[#0B132B] shadow-[0_0_40px_rgba(255,215,0,0.6)] scale-105'
                      : 'bg-gradient-to-br from-[#1C2541] to-[#2A3B5C] text-[#EAEAEA] border-2 border-[#C6A15B]/30 hover:border-[#C6A15B]/60'
                  }`}
                >
                  <span className="block sm:inline">📋 {customMonth ? new Date(customMonth.year, customMonth.month - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Choisir Un Mois'}</span>
                  <div className="text-xs font-normal mt-1 opacity-80">
                    {customMonth ? 'Période sélectionnée' : 'Sélectionner...'}
                  </div>
                </button>
              </div>

              {/* Calendrier des Mois - CENTRÉ ET VISIBLE */}
              {showMonthDropdown && (
                <>
                  <div 
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[99998] animate-in fade-in duration-200 flex items-center justify-center p-4" 
                    onClick={() => setShowMonthDropdown(false)}
                  >
                    <div 
                      className="w-full max-w-[90vw] sm:max-w-[600px] md:max-w-[700px] max-h-[90vh] overflow-y-auto z-[99999] animate-in zoom-in-95 duration-300"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700] rounded-3xl blur-2xl opacity-30 animate-pulse pointer-events-none"></div>
                      
                      <div className="relative bg-gradient-to-br from-[#1C2541] via-[#2A3B5C] to-[#1C2541] border-3 border-[#C6A15B]/60 rounded-3xl shadow-2xl overflow-hidden">
                        {/* Header Ultra Premium */}
                        <div className="px-4 sm:px-6 py-4 border-b-2 border-[#C6A15B]/40 bg-gradient-to-r from-[#C6A15B]/10 to-[#FFD700]/10 sticky top-0 z-20 backdrop-blur-sm">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-xl sm:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B]">
                                📅 Choisir Un Mois
                              </h3>
                              <p className="text-[#C6A15B]/80 text-xs sm:text-sm mt-1">Sélectionnez le mois souhaité</p>
                            </div>
                            <button 
                              onClick={() => setShowMonthDropdown(false)}
                              className="text-[#EAEAEA] hover:text-[#FFD700] hover:bg-[#FFD700]/10 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 text-3xl font-bold flex-shrink-0"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      
                      {/* Calendrier des Mois en Grille - Avec padding bottom */}
                      <div className="p-4 sm:p-6 pb-6 sm:pb-8">
                        {/* Année en Cours */}
                        <div className="mb-4 sm:mb-6">
                          <h4 className="text-[#FFD700] font-black text-base sm:text-lg mb-3 sm:mb-4 flex items-center gap-2">
                            <span className="text-xl sm:text-2xl">🗓️</span>
                            {new Date().getFullYear()}
                          </h4>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                            {[...Array(12)].map((_, i) => {
                              const monthIndex = i + 1;
                              const now = new Date();
                              const currentMonth = now.getMonth() + 1;
                              const currentYear = now.getFullYear();
                              
                              // Afficher UNIQUEMENT les mois déjà écoulés AVANT le mois précédent
                              // Exclure : mois en cours, mois précédent, et tous les mois futurs
                              const isExcluded = monthIndex >= (currentMonth - 1);
                              
                              if (isExcluded) return null;
                              
                              const monthName = new Date(currentYear, i, 1).toLocaleDateString('fr-FR', { month: 'long' });
                              const isSelected = customMonth?.year === currentYear && customMonth?.month === monthIndex;
                              
                              return (
                                <button
                                  key={monthIndex}
                                  onClick={() => {
                                    setCustomMonth({ year: currentYear, month: monthIndex });
                                    setSelectedMonth('custom');
                                    setShowMonthDropdown(false);
                                  }}
                                  className={`group relative px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:scale-105 ${
                                    isSelected
                                      ? 'bg-gradient-to-br from-[#FFD700] to-[#C6A15B] text-[#0B132B] shadow-lg shadow-[#FFD700]/50'
                                      : 'bg-gradient-to-br from-[#2A3B5C]/50 to-[#1C2541]/50 text-[#EAEAEA] border-2 border-[#C6A15B]/30 hover:border-[#FFD700]/60 hover:bg-gradient-to-br hover:from-[#FFD700]/20 hover:to-[#C6A15B]/20'
                                  }`}
                                >
                                  <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/0 via-[#FFD700]/20 to-[#FFD700]/0 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg sm:rounded-xl"></div>
                                  <span className="relative capitalize">{monthName}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        
                        {/* Année Précédente (si applicable) */}
                        {new Date().getMonth() < 6 && (
                          <div className="pb-2">
                            <h4 className="text-[#C6A15B] font-black text-base sm:text-lg mb-3 sm:mb-4 flex items-center gap-2">
                              <span className="text-xl sm:text-2xl">📆</span>
                              {new Date().getFullYear() - 1}
                            </h4>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                              {[...Array(12)].map((_, i) => {
                                const monthIndex = i + 1;
                                const lastYear = new Date().getFullYear() - 1;
                                
                                // Pour l'année précédente, afficher TOUS les 12 mois
                                // car ils sont tous déjà écoulés
                                
                                const monthName = new Date(lastYear, i, 1).toLocaleDateString('fr-FR', { month: 'long' });
                                const isSelected = customMonth?.year === lastYear && customMonth?.month === monthIndex;
                                
                                return (
                                  <button
                                    key={`${lastYear}-${monthIndex}`}
                                    onClick={() => {
                                      setCustomMonth({ year: lastYear, month: monthIndex });
                                      setSelectedMonth('custom');
                                      setShowMonthDropdown(false);
                                    }}
                                    className={`group relative px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-xs sm:text-sm md:text-base transition-all duration-300 transform hover:scale-105 ${
                                      isSelected
                                        ? 'bg-gradient-to-br from-[#FFD700] to-[#C6A15B] text-[#0B132B] shadow-lg shadow-[#FFD700]/50'
                                        : 'bg-gradient-to-br from-[#2A3B5C]/50 to-[#1C2541]/50 text-[#EAEAEA] border-2 border-[#C6A15B]/30 hover:border-[#FFD700]/60 hover:bg-gradient-to-br hover:from-[#FFD700]/20 hover:to-[#C6A15B]/20'
                                    }`}
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/0 via-[#FFD700]/20 to-[#FFD700]/0 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg sm:rounded-xl"></div>
                                    <span className="relative capitalize">{monthName}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                </>
              )}

              {/* Mois Précédent - RESPONSIVE */}
              <button
                onClick={() => setSelectedMonth('previous')}
                className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-sm sm:text-base transition-all duration-300 transform hover:scale-105 ${
                  selectedMonth === 'previous'
                    ? 'bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700] text-[#0B132B] shadow-[0_0_40px_rgba(255,215,0,0.6)] scale-105'
                    : 'bg-gradient-to-br from-[#1C2541] to-[#2A3B5C] text-[#EAEAEA] border-2 border-[#C6A15B]/30 hover:border-[#C6A15B]/60'
                }`}
              >
                <span className="block sm:inline">📆 Mois Précédent</span>
                <div className="text-xs font-normal mt-1 opacity-80">
                  {new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </div>
              </button>

              {/* Mois en Cours - RESPONSIVE */}
              <button
                onClick={() => setSelectedMonth('current')}
                className={`w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-sm sm:text-base transition-all duration-300 transform hover:scale-105 ${
                  selectedMonth === 'current'
                    ? 'bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700] text-[#0B132B] shadow-[0_0_40px_rgba(255,215,0,0.6)] scale-105'
                    : 'bg-gradient-to-br from-[#1C2541] to-[#2A3B5C] text-[#EAEAEA] border-2 border-[#C6A15B]/30 hover:border-[#C6A15B]/60'
                }`}
              >
                <span className="block sm:inline">📆 Mois en Cours</span>
                <div className="text-xs font-normal mt-1 opacity-80">
                  {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </div>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-[#C6A15B]/30 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-[#C6A15B] border-t-transparent rounded-full animate-spin absolute top-0"></div>
          </div>
        </div>
      ) : (
        <>
          {/* Total Summary Card PREMIUM */}
          {(transactions.length > 0 || tournaments.length > 0) && (
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/10 to-[#C6A15B]/10 rounded-2xl blur-xl"></div>
              <Card className="relative glass-effect border-2 border-[#C6A15B]/30 shadow-2xl">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="text-center sm:text-left">
                      <p className="text-[#C6A15B] text-sm font-semibold mb-2 uppercase tracking-wider">📊 Nombre de transactions</p>
                      <p className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B]">
                        {transactions.length + tournaments.length}
                      </p>
                    </div>
                    <div className="h-16 w-px bg-[#C6A15B]/30 hidden sm:block"></div>
                    <div className="text-center sm:text-right">
                      <p className="text-[#C6A15B] text-sm font-semibold mb-2 uppercase tracking-wider">💰 Total Général</p>
                      <p className="text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700]">
                        {(
                          transactions.reduce((sum, t) => sum + t.total, 0) +
                          tournaments.reduce((sum, t) => sum + t.gainPlayCity, 0)
                        ).toLocaleString()} <span className="text-2xl">FCFA</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tournaments Section PREMIUM */}
          {tournaments.length > 0 && (selectedRubrique === 'all' || selectedRubrique === 'tournoi') && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-[#FFD700] to-[#C6A15B] rounded-xl shadow-xl">
                  <Trophy className="w-6 h-6 text-[#0B132B]" strokeWidth={3} />
                </div>
                <h2 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B]">
                  🏆 Tournois
                </h2>
              </div>
              {tournaments.map(tournament => (
                <div key={tournament.id} className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/10 to-[#C6A15B]/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
                  <Card className="relative glass-effect border-2 border-[#C6A15B]/30 hover:border-[#FFD700]/60 transition-all hover:shadow-2xl hover:shadow-[#FFD700]/20 hover:scale-[1.02]">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B] mb-2">
                              {tournament.nomTournoi}
                            </h3>
                            <p className="text-[#EAEAEA]/80 text-sm flex items-center gap-2 mb-1">
                              📅 {new Date(tournament.date).toLocaleString('fr-FR')}
                            </p>
                            {tournament.commercialName && (
                              <p className="text-[#C6A15B] text-sm font-bold flex items-center gap-2">
                                👤 Organisé par: {tournament.commercialName}
                              </p>
                            )}
                          </div>
                          {user?.role === 'admin' && (
                            <Button
                              onClick={() => {
                                setSelectedTournament(tournament);
                                setShowDeleteTournamentDialog(true);
                              }}
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:text-red-600 hover:bg-red-400/20 transition-all"
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                          <div className="bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] p-4 rounded-xl border border-[#C6A15B]/20 hover:border-[#C6A15B]/50 transition-all">
                            <p className="text-[#C6A15B]/70 text-xs font-semibold mb-1 uppercase tracking-wider">👥 Joueurs</p>
                            <p className="text-[#EAEAEA] font-black text-2xl">{tournament.nombreJoueurs}</p>
                          </div>
                          <div className="bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] p-4 rounded-xl border border-[#C6A15B]/20 hover:border-[#C6A15B]/50 transition-all">
                            <p className="text-[#C6A15B]/70 text-xs font-semibold mb-1 uppercase tracking-wider">💰 Total</p>
                            <p className="text-[#C6A15B] font-black text-xl">{tournament.total.toLocaleString()} <span className="text-sm">FCFA</span></p>
                          </div>
                          <div className="bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] p-4 rounded-xl border border-yellow-500/20 hover:border-yellow-500/50 transition-all">
                            <p className="text-yellow-500/70 text-xs font-semibold mb-1 uppercase tracking-wider">🥇 1er (40%)</p>
                            <p className="text-yellow-400 font-black text-xl">{tournament.gainVainqueur.toLocaleString()} <span className="text-sm">FCFA</span></p>
                          </div>
                          <div className="bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] p-4 rounded-xl border border-gray-400/20 hover:border-gray-400/50 transition-all">
                            <p className="text-gray-400/70 text-xs font-semibold mb-1 uppercase tracking-wider">🥈 2ème (20%)</p>
                            <p className="text-gray-300 font-black text-xl">{tournament.gainDeuxieme.toLocaleString()} <span className="text-sm">FCFA</span></p>
                          </div>
                        </div>
                        
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700] to-[#C6A15B] rounded-xl blur-sm opacity-20"></div>
                          <div className="relative bg-gradient-to-r from-[#FFD700]/20 to-[#C6A15B]/20 border-2 border-[#FFD700] p-4 rounded-xl">
                            <div className="flex items-center justify-between">
                              <p className="text-[#FFD700] text-sm font-bold uppercase tracking-wider">🏢 PlayCity (40%)</p>
                              <p className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B]">
                                {tournament.gainPlayCity.toLocaleString()} <span className="text-lg">FCFA</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}

          {/* Transactions Section PREMIUM */}
          <div className="space-y-4">
            {transactions.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-[#3B82F6] to-[#2563EB] rounded-xl shadow-xl">
                  <FileText className="w-6 h-6 text-white" strokeWidth={3} />
                </div>
                <h2 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#3B82F6] to-[#2563EB]">
                  💳 Ventes & Sessions
                </h2>
              </div>
            )}
            {transactions.map(transaction => {
              // Get types of sales from items - libelle contient déjà la durée
              const salesTypes = transaction.items?.map(item => item.libelle).join(', ') || 'N/A';
              
              // Utiliser directement salesTypes (qui contient déjà la durée si applicable)
              const mainTitle = salesTypes;
              
              return (
            <div key={transaction.id} className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-[#3B82F6]/10 to-[#2563EB]/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <Card className={`relative glass-effect border-2 ${transaction.canceled ? 'opacity-50 border-red-500/30' : 'border-[#3B82F6]/30 hover:border-[#3B82F6]/60'} hover:shadow-2xl hover:shadow-[#3B82F6]/20 hover:scale-[1.02] transition-all`}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* LEFT SECTION - Informations détaillées */}
                    <div className="flex-1 space-y-3">
                      {/* Titre principal avec statut */}
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl sm:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#3B82F6] to-[#2563EB]">
                          {mainTitle}
                        </h3>
                        {transaction.canceled && (
                          <span className="bg-red-600 text-white text-xs px-3 py-1 rounded-full font-bold">ANNULÉ</span>
                        )}
                      </div>

                      {/* Grille d'informations - Style carte de jeu */}
                      <div className="space-y-2">
                        {/* Date et heure */}
                        <div className="flex items-center gap-2 text-[#EAEAEA]/90">
                          <span className="text-[#3B82F6]">📅</span>
                          <span className="font-medium">{new Date(transaction.date).toLocaleString('fr-FR')}</span>
                        </div>

                        {/* Client - MIS EN ÉVIDENCE */}
                        <div className="flex items-center gap-2">
                          <span className="text-[#3B82F6]">👤</span>
                          <span className="text-[#EAEAEA] font-bold text-base">
                            Client: <span className="text-white">
                              {(() => {
                                const nom = transaction.client?.nom?.trim();
                                const prenom = transaction.client?.prenom?.trim();
                                
                                if (nom && prenom) {
                                  // Si NOM et PRÉNOM remplis → Afficher "PRÉNOM NOM"
                                  return `${prenom} ${nom}`;
                                } else if (nom) {
                                  // Si seulement NOM rempli → Afficher "NOM"
                                  return nom;
                                } else if (prenom) {
                                  // Si seulement PRÉNOM rempli → Afficher "PRÉNOM"
                                  return prenom;
                                } else {
                                  return 'N/A';
                                }
                              })()}
                            </span>
                          </span>
                        </div>

                        {/* Jeu joué */}
                        {transaction.client?.jeux && (
                          <div className="flex items-center gap-2">
                            <span className="text-[#3B82F6]">🎮</span>
                            <span className="text-[#EAEAEA]/90 font-medium">
                              Jeu: <span className="text-white">{transaction.client.jeux}</span>
                            </span>
                          </div>
                        )}

                        {/* Mode de paiement */}
                        <div className="flex items-center gap-2">
                          <span className="text-[#3B82F6]">💳</span>
                          <span className="text-[#EAEAEA]/90 font-medium">
                            Mode: <span className="text-white">{transaction.modePaiement}</span>
                          </span>
                        </div>

                        {/* Commercial - EN OR CHAMPAGNE ⭐ */}
                        {transaction.commercialName && (
                          <div className="flex items-center gap-2 mt-2 bg-gradient-to-r from-[#C6A15B]/10 to-[#FFD700]/10 rounded-lg p-2 border border-[#C6A15B]/30">
                            <span className="text-[#FFD700]">🏆</span>
                            <span className="font-bold text-base">
                              Commercial: <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700] font-black">{transaction.commercialName}</span>
                            </span>
                          </div>
                        )}

                        {/* Numéro de reçu */}
                        <div className="flex items-center gap-2 text-[#EAEAEA]/60 text-sm mt-2">
                          <span className="text-[#3B82F6]">📄</span>
                          <span>Reçu N°: {transaction.numeroRecu}</span>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT SECTION - Montant et Actions */}
                    <div className="flex flex-col sm:flex-row lg:flex-col items-stretch sm:items-center lg:items-end gap-4">
                      <div className="text-center sm:text-right bg-gradient-to-br from-[#3B82F6]/10 to-[#2563EB]/10 rounded-xl p-4 sm:p-5 border border-[#3B82F6]/30">
                        <p className="text-[#3B82F6] text-xs font-semibold mb-1 uppercase tracking-wider">Montant</p>
                        <div className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#3B82F6] to-[#2563EB]">
                          {transaction.total.toLocaleString()}
                        </div>
                        <span className="text-[#3B82F6] font-bold text-lg">FCFA</span>
                      </div>
                      <div className="flex flex-col gap-2 min-w-[140px]">
                        <Button
                          data-testid={`view-receipt-btn-${transaction.id}`}
                          onClick={() => handleViewReceipt(transaction.id)}
                          size="sm"
                          className="bg-gradient-to-r from-[#1C2541] to-[#2A3B5C] hover:from-[#C6A15B] hover:to-[#B89149] text-[#EAEAEA] hover:text-[#0B132B] border border-[#C6A15B]/30 transition-all font-bold"
                        >
                          <ImageIcon className="w-4 h-4 mr-2" />
                          Voir Reçu
                        </Button>
                        {user?.role === 'admin' && (
                          <>
                            <Button
                              onClick={() => {
                                setSelectedTransaction(transaction);
                                setSelectedDate(new Date(transaction.date));
                                setShowEditDateDialog(true);
                              }}
                              size="sm"
                              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white w-full transition-all hover:scale-105 font-bold"
                            >
                              <Calendar className="w-4 h-4 mr-2" />
                              Modifier Date
                            </Button>
                            <Button
                              onClick={() => {
                                setSelectedTransaction(transaction);
                                setShowDeleteDialog(true);
                              }}
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white w-full transition-all hover:scale-105 font-bold"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
            })}
          </div>
        </>
      )}

      {/* Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[#C6A15B]">Reçu de vente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {receiptImage && (
              <div className="bg-white rounded-lg p-4">
                <img src={receiptImage} alt="Reçu" className="w-full" />
              </div>
            )}
            <Button
              onClick={() => setShowReceiptDialog(false)}
              className="w-full bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
            >
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-red-400">⚠️ Suppression Définitive</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#EAEAEA]">
              <strong>ATTENTION :</strong> Cette action est irréversible ! La vente sera définitivement supprimée de l'historique et décomptée de tous les totaux.
            </p>
            {selectedTransaction && (
              <div className="bg-[#0B132B] p-3 rounded border-2 border-red-500">
                <p className="text-[#C6A15B] font-bold">Reçu N°: {selectedTransaction.numeroRecu}</p>
                <p className="text-[#EAEAEA]">{selectedTransaction.total.toLocaleString()} FCFA</p>
                <p className="text-[#EAEAEA]/60 text-sm">
                  Client: {selectedTransaction.client?.prenom} {selectedTransaction.client?.nom}
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteDialog(false)}
                className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA]"
              >
                Annuler
              </Button>
              <Button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Supprimer Définitivement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteTournamentDialog} onOpenChange={setShowDeleteTournamentDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-red-400">⚠️ Suppression Définitive du Tournoi</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#EAEAEA]">
              <strong>ATTENTION :</strong> Cette action est irréversible ! Le tournoi sera définitivement supprimé et décompté de tous les totaux du Dashboard.
            </p>
            {selectedTournament && (
              <div className="bg-[#0B132B] p-3 rounded border-2 border-red-500">
                <p className="text-[#C6A15B] font-bold">{selectedTournament.nomTournoi}</p>
                <p className="text-[#EAEAEA]">Gain PlayCity: {selectedTournament.gainPlayCity?.toLocaleString()} FCFA</p>
                {selectedTournament.commercialName && (
                  <p className="text-[#EAEAEA]/60 text-sm">Organisé par: {selectedTournament.commercialName}</p>
                )}
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteTournamentDialog(false)}
                className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA]"
              >
                Annuler
              </Button>
              <Button
                onClick={handleDeleteTournament}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Supprimer Définitivement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDateDialog} onOpenChange={setShowEditDateDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] max-w-[95vw] sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#C6A15B] text-xl sm:text-2xl font-black flex items-center gap-2">
              <span>📅</span>
              <span>Modifier la Date</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4">
            {selectedTransaction && (
              <div className="bg-gradient-to-br from-[#0B132B] to-[#1C2541]/50 p-3 rounded-xl border-2 border-[#C6A15B]/30 shadow-lg">
                <p className="text-[#FFD700] font-bold text-sm sm:text-base mb-1">Reçu: {selectedTransaction.numeroRecu}</p>
                <p className="text-[#EAEAEA] text-lg sm:text-xl font-black">{selectedTransaction.total.toLocaleString()} FCFA</p>
                <p className="text-[#EAEAEA]/60 text-xs sm:text-sm mt-1">
                  Client: {selectedTransaction.client?.prenom} {selectedTransaction.client?.nom}
                </p>
                <div className="mt-2 pt-2 border-t border-[#C6A15B]/20">
                  <p className="text-[#3B82F6] text-xs sm:text-sm font-semibold">
                    📅 Date: {new Date(selectedTransaction.date).toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            )}
            
            <div className="bg-gradient-to-br from-[#2A3B5C]/80 to-[#1C2541]/80 p-3 rounded-xl border border-[#FFD700]/30">
              <Label className="text-[#FFD700] font-semibold mb-2 block">📅 Date de la transaction</Label>
              {isMobile ? (
                <input
                  type="date"
                  value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    if (selectedDate) {
                      newDate.setHours(selectedDate.getHours());
                      newDate.setMinutes(selectedDate.getMinutes());
                    }
                    setSelectedDate(newDate);
                    setTimeout(() => handleEditDate(newDate), 300);
                  }}
                  className="w-full bg-[#0B132B]/80 border-2 border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700] transition-colors text-base font-bold rounded-lg px-4 py-2.5 cursor-pointer"
                  data-testid="historique-date-mobile"
                  style={{ colorScheme: 'dark' }}
                />
              ) : (
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => {
                    const newDate = new Date(date);
                    newDate.setHours(selectedDate.getHours());
                    newDate.setMinutes(selectedDate.getMinutes());
                    setSelectedDate(newDate);
                    setTimeout(() => {
                      handleEditDate(newDate);
                    }, 300);
                  }}
                  dateFormat="dd/MM/yyyy"
                  locale="fr"
                  className="w-full bg-[#0B132B]/80 border-2 border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700] transition-colors text-base font-bold rounded-lg px-4 py-2 cursor-pointer"
                  calendarClassName="calendar-premium-popup"
                  showPopperArrow={false}
                  popperPlacement="bottom-start"
                  portalId="datepicker-portal-root"
                  popperProps={{ strategy: 'fixed' }}
                />
              )}
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => {
                  setShowEditDateDialog(false);
                  setSelectedTransaction(null);
                  setSelectedDate(new Date());
                }}
                className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA] border border-[#C6A15B]/30 font-bold"
              >
                Annuler
              </Button>
              <Button
                onClick={handleEditDate}
                disabled={!selectedDate}
                className="flex-1 bg-gradient-to-r from-[#C6A15B] to-[#B89149] hover:from-[#B89149] hover:to-[#C6A15B] text-[#0B132B] font-black shadow-lg shadow-[#C6A15B]/50 disabled:opacity-50 hover:scale-105 transition-all"
              >
                ✅ Confirmer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Historique;
